import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import useSencoPupils from '../../hooks/useSencoPupils';
import { getOrgConfig } from '../../utils/orgResolution';
import { apiFamilyPathwayConsent } from '../../utils/rmsApi';
import { familyConsentVersion } from '../../constants/consentVersions';
import { STATUS } from '../../utils/referralStatus';
import {
  canConfirmSubmission,
  isReferralPathwayActive,
  recordRequiresSubmissionConfirmation,
} from '../../utils/submissionConfirmation';
import { pathwayTriageDocuments } from '../../utils/triagePathways';
import { formatLongDate, isSecondaryPupil, isSenPathwayEligible, letterSentAt, viewLetterLabel } from '../../utils/harbourPupils';
import HarbourReferralBlock from '../../components/harbour/HarbourReferralBlock';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourPathwayMark from '../../components/harbour/HarbourPathwayMark';
import HarbourStageStrip from '../../components/harbour/HarbourStageStrip';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourModal from '../../components/harbour/HarbourModal';
import HarbourToast from '../../components/harbour/HarbourToast';
import {
  FAMILY_SERVICE_NAME,
  buildFamilyChild,
  familyFormChip,
  familyUserName,
  referralConcernWords,
  stopChipStatus,
  schoolStillToFinishLine,
} from '../../utils/familyPortal';
import FamilyPortalShell from './FamilyPortalShell';
import FamilyLoadError from './FamilyLoadError';
import FamilyLoadingState from './FamilyLoadingState';
import FamilyCrisisLine from './FamilyCrisisLine';
import FamilyChildCard from './FamilyChildCard';
import FamilyConfirmSheet from './FamilyConfirmSheet';
import FamilyProfileSharing from './FamilyProfileSharing';
import FamilyWithdrawConsent, { withdrawModeFor, senWithdrawModeFor } from './FamilyWithdrawConsent';
import useIsDesktop from './useIsDesktop';
import useMyReferralDocuments, { useDocumentRemoval } from '../../hooks/useMyReferralDocuments';
import HarbourDocumentsBlock from '../../components/harbour/HarbourDocumentsBlock';
import { REMOVE_CONFIRM, REMOVE_TITLE, documentsCountLabel, removeBody } from '../../utils/referralDocumentPortal';
import './familyYourChild.css';
import './familyDocuments.css';

/*
 * Family portal · Your child (PC-05).
 *
 * Mobile (440 frames): one card per pathway, stacked — title, stage track
 * (SEN), at most two status rows with chips on their own line, then the
 * action at the foot. Once referrals are sent, the ADHD and autism rows
 * replace the status rows (inset list rows).
 *
 * Desktop (1720 frames): the child record opens as a record flyout over Your
 * children (Harbour/Flyout, width 1080) — white pathway section cards on the
 * canvas, each with the large stage strip and canvas-tinted form rows where
 * the parent row carries the action; once sent, one referral block per
 * pathway (its own strip plus the referral row), with the receipt line under
 * a sent referral; then the service contact and crisis cards side by side.
 *
 * On every width only one action is gold; every other action is the plum
 * outline. A pathway the school has not set up is shown, dashed and muted,
 * never hidden (Tom 17 Sep).
 */

const SERVICE_NAME = FAMILY_SERVICE_NAME;
// What the SEND pathway is for, under its heading (3434:7768, 3309:1884).
const SEND_PURPOSE_LINE = 'This is for a strengths and needs profile.';
// Who answers questions about the SEND pathway (TDI-502). The referral
// service cannot, and the pilot SOP routes these to the school.
const sendContactLine = (name) =>
  `If you have questions about this, speak to the SENCO at ${name}'s school.`;
const SERVICE_EMAIL = 'bfwh.options4cyp@nhs.net';
const SERVICE_PHONE_LABEL = '0800 121 7762 (Option 5)';
const SERVICE_PHONE = 'tel:08001217762';

/** Whether this pathway's letter can be opened by the guardian. */
function letterShareable(patient, pathwayKey) {
  const td = pathwayTriageDocuments(patient, pathwayKey) || patient?.triageDocuments || {};
  const hasPdf = Boolean(td?.documentPaths?.pdf);
  const sharedRoles = Array.isArray(td?.sharedRoles) ? td.sharedRoles : [];
  return hasPdf && sharedRoles.includes('guardian');
}

/*
 * The referral service's contact details (TDI-502). This belongs to the
 * referral card and nowhere else: BTH cannot answer SEND questions, and the
 * pilot SOP sends those to the school's SENCO. Rendered inside the referral
 * section so a parent reading a child on both pathways cannot take it as
 * covering the SEND card above.
 */
function ServiceContact() {
  return (
    <div className="family-service-contact family-service-contact--in-card">
      <p>If you have questions, contact the team</p>
      <a href={`mailto:${SERVICE_EMAIL}`}>
        <HarbourIcon name="email" size={20} />
        {SERVICE_EMAIL}
      </a>
      <a href={SERVICE_PHONE}>
        <HarbourIcon name="phone" size={20} />
        {SERVICE_PHONE_LABEL}
      </a>
    </div>
  );
}

function StatusRow({ label, chip }) {
  if (!chip) return null;
  return (
    <div className="family-status-row">
      <span className="family-status-row__label">{label}</span>
      <HarbourStatusChip status={chip.status} tone={chip.tone || 'neutral'} label={chip.label} />
    </div>
  );
}

function CardAction({ gold, to, children, ariaLabel, inline = false, arrow = true }) {
  return (
    <Link
      to={to}
      aria-label={ariaLabel}
      className={[
        'family-card-action',
        gold ? 'family-card-action--gold' : 'family-card-action--secondary',
        inline ? 'family-card-action--inline' : '',
      ].filter(Boolean).join(' ')}
    >
      <span>{children}</span>
      {arrow && <HarbourIcon name="arrow-right" size={20} />}
    </Link>
  );
}

/** Desktop record row: label column, chip, action right (Harbour/Form row). */
function RecordRow({ label, chip, action }) {
  return (
    <div className="harbour-record-row family-record-row">
      {label && <span className="harbour-record-row__label family-record-row__label">{label}</span>}
      {chip && <HarbourStatusChip status={chip.status} tone={chip.tone || 'neutral'} label={chip.label} />}
      <div className="family-record-row__actions">{action}</div>
    </div>
  );
}

export default function FamilyYourChildPage() {
  const navigate = useNavigate();
  const { patientRecordId } = useParams();
  const { user, claims } = useAuth();
  const claimsOrgId = claims?.rmsOrgId;
  const orgId = claimsOrgId || getOrgConfig().orgId;
  const { pupils, loading, error, reload } = useSencoPupils({ orgId, claimsOrgId });
  const isDesktop = useIsDesktop();

  // Give consent again (TDI-478): a family that withdrew consent for the
  // referral can give it again from here, and the referral carries on from
  // where it stopped. The server does the restoring (rmsFamilyPathwayConsent
  // 'agree' on a withdrawn referral); this is only the asking.
  // Which pathway the re-consent dialog is asking about: null when closed,
  // 'referral' (TDI-478) or 'sen' (TDI-479).
  const [reconsentOpen, setReconsentOpen] = useState(null);
  const [reconsentBusy, setReconsentBusy] = useState(false);
  const [reconsentFailed, setReconsentFailed] = useState(null);
  const [reconsentToast, setReconsentToast] = useState(false);

  useEffect(() => {
    if (claims && claims.userType && claims.userType !== 'RMS Legal Guardian') {
      navigate('/rms/dashboard', { replace: true });
    }
  }, [claims, navigate]);

  const pupil = useMemo(
    () => pupils.find((p) => p.id === patientRecordId) || null,
    [pupils, patientRecordId]
  );
  const child = useMemo(() => buildFamilyChild(pupil), [pupil]);
  // A child outside the SEND pathway's year groups was never the school's to
  // start: "Not started by the school" read as something the school had not
  // done (Year 8, dev, 2 Nov). Same sentence the SENCO's record shows.
  const senUnavailableLine = pupil && pupil.yearGroup && !isSenPathwayEligible(pupil)
    ? `The SEND pathway in this pilot is for Years 1 to 6, so it is not offered for ${pupil.yearGroup}.`
    : 'Not started by the school';
  const allChildren = useMemo(() => pupils.map(buildFamilyChild).filter(Boolean), [pupils]);
  const patient = pupil?.patient || {};

  // The family's own documents on the referral (TDI-579): only on a referral
  // that is running (never the SEND-only pathway, never a stopped one), and
  // only ever their own files, from the server.
  const location = useLocation();
  const documentsOn = Boolean(child && child.referral && !child.referral.stopped);
  const documents = useMyReferralDocuments({ orgId, patientRecordId, enabled: documentsOn });
  const [documentNotice, setDocumentNotice] = useState(() => (
    location.state && location.state.documentNotice
      ? { kind: 'info', title: location.state.documentNotice }
      : null
  ));
  const noticeInHistory = Boolean(location.state && location.state.documentNotice);
  useEffect(() => {
    // Shown once: a reload of the record does not announce it again.
    if (noticeInHistory) navigate(location.pathname, { replace: true, state: { stay: true } });
  }, [noticeInHistory, navigate, location.pathname]);
  const removal = useDocumentRemoval({ remove: documents.remove, reload: documents.reload, onNotice: setDocumentNotice });

  const closeReconsent = useCallback(() => {
    setReconsentOpen(null);
    setReconsentFailed(null);
  }, []);

  const giveConsentAgain = useCallback(async () => {
    setReconsentBusy(true);
    setReconsentFailed(null);
    try {
      // The same route as the first consent, with the wording version this
      // screen's decision rests on (TDI-480). The server puts the referral
      // back where it stopped.
      const pathway = reconsentOpen === 'sen' ? 'sen' : 'referral';
      await apiFamilyPathwayConsent({
        orgId, patientRecordId, pathway, decision: 'agree',
        consentVersion: familyConsentVersion(pathway),
      });
      setReconsentOpen(null);
      setReconsentToast(true);
      if (typeof reload === 'function') reload({ silent: true });
    } catch (e) {
      // A refusal is not a connection problem: do not tell the parent to
      // check their internet when the platform said no.
      setReconsentFailed(String(e?.code || '').includes('permission-denied') ? 'refused' : 'network');
    } finally {
      setReconsentBusy(false);
    }
  }, [orgId, patientRecordId, reload, reconsentOpen]);

  const userName = familyUserName(user, pupils);
  const back = { to: '/rms/family/children', label: 'Back' };
  const closeToChildren = () => navigate('/rms/family/children', { state: { stay: true } });

  if (loading) {
    return (
      <FamilyPortalShell userName={userName} back={back}>
        <FamilyLoadingState />
      </FamilyPortalShell>
    );
  }
  if (error || (!loading && !pupil)) {
    return (
      <FamilyPortalShell userName={userName} back={back}>
        <FamilyLoadError onRetry={() => reload()} />
      </FamilyPortalShell>
    );
  }

  const sen = child.sen;
  const referral = child.referral;
  const firstName = child.firstName;
  // Only a withdrawal can be taken back. A refusal ("I do not agree") stopped
  // the pathway before it began, and the school starts again from the top.
  const refWithdrawn = Boolean(referral && referral.stopped && referral.stopReason === 'consent_withdrawn');
  // The SEND pathway's equivalent (TDI-479): the family took their permission
  // back, and keeps what was done. Before the profile that is their answers;
  // after it, the profile too.
  const senWithdrawn = Boolean(sen && sen.stopped && sen.stopReason === 'consent_withdrawn');
  const senWithdrawMode = sen ? senWithdrawModeFor({ stopped: sen.stopped, consent: sen.consent }) : null;
  // Who the family contacts to leave the SEND pathway the other way (E-3):
  // the real SENCO and school on the record.
  const referrer = patient.referrer || {};
  const schoolContact = {
    sencoName: referrer.fullName || [referrer.firstName, referrer.lastName].filter(Boolean).join(' '),
    schoolName: patient.school?.schoolName || patient.schoolName || '',
    phone: referrer.phone || '',
  };
  const school = patient.school?.schoolName || patient.schoolName || 'their school';

  // ── Per-card facts ────────────────────────────────────────────────────────
  const senParentChip = sen && familyFormChip(pupil.senGuardianStatus);
  const senSchoolChip = sen && familyFormChip(pupil.senSchoolStatus);
  const senBothDone = Boolean(
    sen &&
    pupil.senGuardianStatus === STATUS.COMPLETED &&
    pupil.senSchoolStatus === STATUS.COMPLETED
  );
  const senReportPending = Boolean(sen && senBothDone && !sen.reportAvailable && !sen.stopped);
  const senConsentNeeded = Boolean(sen && !sen.stopped && sen.consent === 'needed');
  const senFormsToDo = Boolean(
    sen && !sen.stopped && sen.consent === 'confirmed' && !sen.reportAvailable &&
    pupil.senGuardianStatus !== STATUS.COMPLETED
  );
  const senFormsLabel = pupil?.senGuardianStatus === STATUS.STARTED ? 'Continue' : 'Start';

  const refParentChip = referral && familyFormChip(pupil.guardianStatus);
  const refSchoolChip = referral && familyFormChip(pupil.schoolStatus);
  const refSent = Boolean(
    referral && !referral.stopped &&
    (referral.pathways || []).some((p) =>
      ['sentWaitingForReview', 'inReview', 'outcomeAvailable', 'closed'].includes(p.status))
  );
  const refConsentNeeded = Boolean(referral && !referral.stopped && referral.consent === 'needed');
  const refFormsToDo = Boolean(
    referral && !referral.stopped && !refSent && referral.consent === 'confirmed' &&
    pupil.guardianStatus !== STATUS.COMPLETED
  );
  const refFormsLabel = pupil?.guardianStatus === STATUS.STARTED ? 'Continue' : 'Start';
  // Every form on a pathway is in but not yet confirmed (TDI-415): that
  // pathway's confirm step is its next thing to do, and nothing is shared or
  // sent until it is done. Each pathway is confirmed on its own (TDI-651),
  // so each card offers its own.
  const confirmNeededOn = (which) => Boolean(
    pupil && recordRequiresSubmissionConfirmation(patient) &&
    (which === 'sen'
      ? Boolean(sen && !sen.stopped)
      : Boolean(referral && !referral.stopped && isReferralPathwayActive(patient))) &&
    canConfirmSubmission({ patient, formProgress: pupil.formProgress || {}, role: 'guardian', pathway: which })
  );
  const senConfirmNeeded = confirmNeededOn('sen');
  const refConfirmNeeded = confirmNeededOn('referral');
  const confirmNeeded = senConfirmNeeded || refConfirmNeeded;
  // The gold action goes to the referral first when both wait, as it did.
  const confirmPathway = refConfirmNeeded ? 'referral' : 'sen';
  const confirmOn = (which) => (which === 'sen' ? senConfirmNeeded : refConfirmNeeded);
  // Until the parent confirms, their finished forms are not sent anywhere,
  // so the chip must not read "Done".
  const READY_TO_SUBMIT = { status: 'inProgress', label: 'Ready to submit' };
  const senParentChipShown = confirmOn('sen') ? READY_TO_SUBMIT : senParentChip;
  const refParentChipShown = confirmOn('referral') ? READY_TO_SUBMIT : refParentChip;

  // Each referral's own letter once it has been sent to the family (TDI-691).
  // A referral not accepted onto the pathway has its letter too, as on the
  // per-referral page; only an accepted outcome's letter takes the gold.
  const letterPathways = refSent
    ? (referral.pathways || []).filter(
        (p) => (p.status === 'outcomeAvailable' || p.status === 'closed') && letterShareable(patient, p.key)
      )
    : [];
  const letterActions = letterPathways.map((p) => p.key);
  const goldLetter = letterPathways.find((p) => p.status === 'outcomeAvailable');
  // "Letter sent 17 July 2026" under a referral whose letter the family can open.
  const letterSentLine = (pathwayKey) => {
    if (!letterActions.includes(pathwayKey)) return null;
    const sentOn = formatLongDate(letterSentAt(patient, pathwayKey));
    return sentOn ? `Letter sent ${sentOn}` : null;
  };

  // ── The screen's single gold action ──────────────────────────────────────
  const goldAction = goldLetter
    ? `letter:${goldLetter.key}`
    : senConsentNeeded ? 'sen:consent'
    : refConsentNeeded ? 'referral:consent'
    : confirmNeeded ? `${confirmPathway}:confirm`
    : senFormsToDo ? 'sen:forms'
    : refFormsToDo ? 'referral:forms'
    // A profile made before the family left the SEND pathway stays theirs to
    // read, and the design keeps it the card's gold action (E-6).
    : sen && sen.reportAvailable && (!sen.stopped || senWithdrawn) ? 'sen:report'
    : null;

  const base = `/rms/family/child/${patientRecordId}`;

  // The re-consent decision (Figma 5329:406871 on desktop, the P-7 sheet on a
  // phone): the same words on both, the gold "Yes, give it again" rightmost
  // or lowest. The failure wording is the consent page's, verbatim.
  const concernWords = referralConcernWords(patient.intakeConcern);
  // The SEND pathway's words come from E-9 (5331:411059).
  const reconsentTitle = reconsentOpen === 'sen'
    ? `Give your permission again for ${firstName}’s SEND pathway?`
    : `Give your permission again for ${firstName}’s ${concernWords ? `${concernWords} ` : ''}referral?`;
  const reconsentBody = (
    <>
      <p className="harbour-body-16">
        {reconsentOpen === 'sen'
          ? 'The SEND pathway will carry on from where it stopped.'
          : 'The referral will carry on from where it stopped.'}
      </p>
      {reconsentFailed && (
        <HarbourNotice kind="error" title="We could not save your answer">
          {reconsentFailed === 'refused'
            ? `Your answer has not been recorded. Please contact ${school} so they can check your details.`
            : 'Check your internet connection and try again. Your answer has not been recorded.'}
        </HarbourNotice>
      )}
    </>
  );
  const reconsentEntry = refWithdrawn && (
    <HarbourButton
      kind="secondary"
      className={isDesktop ? 'family-record__entry-action' : 'family-reconsent__entry'}
      onClick={() => setReconsentOpen('referral')}
    >
      Give your permission again
    </HarbourButton>
  );
  // TDI-799 (SOP A.4, ruling R-054): withdrawing stops the referral but the
  // answers are kept, and the withdrawal step promised "you can still read
  // them" — so the way back must exist. TDI-692's print view of their own
  // answers, saved as a PDF from the browser.
  const answersDownloadEntry = refWithdrawn && (
    <HarbourButton
      kind="secondary"
      leadingIcon="download"
      className={isDesktop ? 'family-record__entry-action' : 'family-reconsent__entry'}
      onClick={() => window.open(`${base}/forms/referral?print=answers`, '_blank', 'noopener')}
    >
      Download your answers
    </HarbourButton>
  );
  const senReconsentEntry = senWithdrawn && (
    <HarbourButton
      kind="secondary"
      className={isDesktop ? 'family-record__entry-action' : 'family-reconsent__entry'}
      onClick={() => setReconsentOpen('sen')}
    >
      Give your permission again
    </HarbourButton>
  );
  // Taking back permission (TDI-473): offered while the referral can still be
  // withdrawn here; past the outcome letter the family is pointed at the
  // service instead. A combined referral is withdrawn as one, so the one
  // entry covers both pathways.
  const withdrawMode = withdrawModeFor({
    stopped: referral?.stopped,
    consent: referral?.consent,
    pathwayStates: (referral?.pathways || []).map((p) => p.status),
  });
  const withdrawUi = withdrawMode && (
    <FamilyWithdrawConsent
      mode={withdrawMode}
      orgId={orgId}
      patientRecordId={patientRecordId}
      childName={firstName}
      concernWords={concernWords}
      pathways={(referral?.pathways || []).map((p) => p.key)}
      isDesktop={isDesktop}
      onWithdrawn={() => { if (typeof reload === 'function') reload({ silent: true }); }}
    />
  );
  // The SEND pathway's own entry (TDI-479, E-1): leaving it here, or being
  // told how to contact the school instead.
  const senWithdrawUi = senWithdrawMode && (
    <FamilyWithdrawConsent
      mode={senWithdrawMode}
      pathway="sen"
      orgId={orgId}
      patientRecordId={patientRecordId}
      childName={firstName}
      schoolContact={schoolContact}
      isDesktop={isDesktop}
      onWithdrawn={() => { if (typeof reload === 'function') reload({ silent: true }); }}
    />
  );
  // What the SEND card shows once the family has left it (E-5 before the
  // profile, E-6 after): the notice, where the pathway got to, what they
  // keep, and the way back.
  const senWithdrawnNotice = (
    <HarbourNotice kind="info" className="family-child-notice" title="You took back your permission for the SEND pathway." />
  );
  const senWithdrawnStage = senWithdrawn ? (sen.reportAvailable ? 'plan' : 'assess') : null;
  const reconsentUi = (
    <>
      {reconsentOpen && (isDesktop ? (
        <HarbourModal
          title={reconsentTitle}
          confirmLabel="Yes, give it again"
          confirmKind="family"
          busy={reconsentBusy}
          onConfirm={giveConsentAgain}
          onClose={closeReconsent}
        >
          {reconsentBody}
        </HarbourModal>
      ) : (
        <FamilyConfirmSheet
          title={reconsentTitle}
          confirmLabel="Yes, give it again"
          busy={reconsentBusy}
          onConfirm={giveConsentAgain}
          onClose={closeReconsent}
        >
          {reconsentBody}
        </FamilyConfirmSheet>
      ))}
      {/* The second line is true since TDI-477: rmsFamilyPathwayConsent emails
          the linked parents or carers and the school on re-consent. */}
      {reconsentToast && (
        <HarbourToast title="Permission given again" onClose={() => setReconsentToast(false)}>
          We&rsquo;ve emailed you and the school.
        </HarbourToast>
      )}
    </>
  );

  // The parent has finished but the school has not: say who (Jasmine, 6 Oct
  // 2026) and what follows. The SEND pathway leads when both are waiting.
  // A pathway the parent still has to submit is waiting on them, not on the
  // school, so it is not among these (TDI-651: each pathway on its own).
  const senWaitingForSchool = Boolean(
    sen && !sen.stopped && !sen.reportAvailable && !senConfirmNeeded &&
    pupil.senGuardianStatus === STATUS.COMPLETED && pupil.senSchoolStatus !== STATUS.COMPLETED
  );
  const refWaitingForSchool = Boolean(
    referral && !referral.stopped && !refSent && !refConfirmNeeded &&
    pupil.guardianStatus === STATUS.COMPLETED && pupil.schoolStatus !== STATUS.COMPLETED
  );
  // TDI-514: a child on both pathways used to be told only about the SEND
  // one, so a family who had just confirmed their referral forms had no way
  // to tell whether the referral was on its way. The two pathways are
  // completely separate (Tom King, 27 October 2026), so each waiting pathway
  // says where it stands.
  const waitingForSchoolLines = [
    senWaitingForSchool
      && `Once they have, you can both see ${firstName}'s profile, with home and school side by side.`,
    refWaitingForSchool
      && 'Once they have, the referral is sent automatically.',
  ].filter(Boolean);
  const waitingForSchoolNotice = waitingForSchoolLines.length > 0 && (
    <HarbourNotice kind="info" className="family-child-notice" title={schoolStillToFinishLine(pupil)}>
      {waitingForSchoolLines.map((line) => <p key={line}>{line}</p>)}
    </HarbourNotice>
  );

  // Each card's confirm opens its own pathway's forms homepage, where the
  // submit pop-up ("Submit your forms · Parent or carer", S-1) is drawn.
  const confirmAction = (which, gold, inline) => (
    <CardAction inline={inline} gold={gold} to={`${base}/forms/${which}`}>
      Confirm and submit
    </CardAction>
  );

  const reportPendingNotice = senReportPending && (
    <HarbourNotice
      kind="info"
      className="family-child-notice"
      title={`When you have both finished, you can both see ${firstName}'s profile, with home and school side by side.`}
    >
      The referral is sent automatically once you and the school have both
      finished your forms.
    </HarbourNotice>
  );

  // ── Desktop: record flyout over Your children ─────────────────────────────
  if (isDesktop) {
    const senAction = senConsentNeeded ? (
      <CardAction inline gold={goldAction === 'sen:consent'} to={`${base}/consent/sen`} arrow={false}>
        Agree to the SEND pathway
      </CardAction>
    ) : senFormsToDo ? (
      <CardAction inline gold={goldAction === 'sen:forms'} to={`${base}/forms/sen`}>{senFormsLabel}</CardAction>
    ) : confirmOn('sen') ? confirmAction('sen', goldAction === 'sen:confirm', true)
    : null;

    const refAction = refConsentNeeded ? (
      <CardAction inline gold={goldAction === 'referral:consent'} to={`${base}/consent/referral`} arrow={false}>
        Agree to the referral
      </CardAction>
    ) : refFormsToDo ? (
      <CardAction inline gold={goldAction === 'referral:forms'} to={`${base}/forms/referral`}>{refFormsLabel}</CardAction>
    ) : confirmOn('referral') ? confirmAction('referral', goldAction === 'referral:confirm', true)
    : null;

    return (
      <FamilyPortalShell userName={userName} maxWidth="full">
        <div className="family-page-head">
          <h1 className="family-page-head__title family-page-head__title--home">Your children</h1>
          <p className="family-page-head__intro">Choose your child’s name to see their details and questions.</p>
        </div>
        <ul className="family-child-cards">
          {allChildren.map((c) => (
            <li key={c.id}><FamilyChildCard child={c} linkState={{ stay: true }} /></li>
          ))}
        </ul>

        <HarbourFlyout
          open
          width="record"
          title={child.name}
          year={child.meta}
          // Close and Escape first dismiss the re-consent dialog if it is
          // open; the record only closes when nothing is asked over it.
          onClose={() => {
            if (reconsentOpen) {
              closeReconsent();
              return;
            }
            if (removal.removing) {
              removal.cancel();
              return;
            }
            closeToChildren();
          }}
          closeLabel="Close this record"
        >
          <div className="family-record">
            {documentNotice && (
              <HarbourNotice kind={documentNotice.kind} title={documentNotice.title} onClose={() => setDocumentNotice(null)}>
                {documentNotice.body || null}
              </HarbourNotice>
            )}
            {reportPendingNotice}
            {waitingForSchoolNotice}

            <section className="harbour-section" aria-label="SEND pathway">
              <div className="harbour-section__heading">
                <HarbourPathwayMark pathway="sen" />
                <h2 className="harbour-section__title">SEND pathway</h2>
              </div>
              {sen ? (
                <>
                  <p className="family-record__for">{SEND_PURPOSE_LINE}</p>
                  {senWithdrawn && senWithdrawnNotice}
                  {(!sen.stopped || senWithdrawn) && (
                    <HarbourStageStrip pathway="sen" currentKey={senWithdrawn ? senWithdrawnStage : sen.stage} />
                  )}
                  {senWithdrawn ? (
                    <>
                      {sen.reportAvailable ? (
                        <RecordRow
                          label={`${firstName}’s profile`}
                          chip={{ status: 'reportAvailable', tone: 'sen' }}
                          action={(
                            <CardAction inline gold={goldAction === 'sen:report'} to={`/rms/patient/${patientRecordId}/hfp`}>
                              View strengths and needs profile
                            </CardAction>
                          )}
                        />
                      ) : (
                        <>
                          <RecordRow
                            label="Parent or carer forms"
                            chip={senParentChipShown && { ...senParentChipShown, tone: 'sen' }}
                            action={<CardAction inline to={`${base}/forms/sen`}>View your answers</CardAction>}
                          />
                          <RecordRow label="School forms" chip={senSchoolChip && { ...senSchoolChip, tone: 'sen' }} />
                        </>
                      )}
                      <RecordRow action={senReconsentEntry} />
                    </>
                  ) : sen.stopped ? (
                    <RecordRow chip={{ status: stopChipStatus(sen.stopReason) }} />
                  ) : sen.reportAvailable ? (
                    <RecordRow
                      label="Strengths and needs profile"
                      chip={{ status: 'reportAvailable', tone: 'sen' }}
                      action={(
                        <CardAction inline gold={goldAction === 'sen:report'} to={`/rms/patient/${patientRecordId}/hfp`}>
                          View strengths and needs profile
                        </CardAction>
                      )}
                    />
                  ) : (
                    <>
                      <RecordRow
                        label="Parent or carer forms"
                        chip={senParentChipShown && { ...senParentChipShown, tone: 'sen' }}
                        action={senAction}
                      />
                      <RecordRow label="School forms" chip={senSchoolChip && { ...senSchoolChip, tone: 'sen' }} />
                    </>
                  )}
                  {senWithdrawUi && <div className="family-record__entry">{senWithdrawUi}</div>}
                  <p className="family-record__receipt">{sendContactLine(firstName)}</p>
                </>
              ) : (
                <p className="family-pathway-card__unavailable-line">{senUnavailableLine}</p>
              )}
            </section>

            <section className="harbour-section" aria-label="Referral pathway">
              <div className="harbour-section__heading">
                <HarbourPathwayMark pathway="referral" />
                <h2 className="harbour-section__title">Referral pathway</h2>
              </div>
              {referral ? (
                <>
                  {referral.forLine && <p className="family-record__for">{referral.forLine}</p>}
                  {referral.stopped ? (
                    <RecordRow chip={{ status: stopChipStatus(referral.stopReason, 'referral') }} />
                  ) : refSent ? (
                    // One box per referral (TDI-691, 3434:8437): its strip,
                    // the receipt line while it waits, and once the letter is
                    // out, the date it was sent and that referral's letter.
                    (referral.pathways || []).map((p) => (
                      <HarbourReferralBlock
                        key={p.key}
                        name={`${p.label} referral`}
                        stageKey={p.status}
                        facts={[
                          p.status === 'sentWaitingForReview' && `${SERVICE_NAME} has received ${firstName}'s referral.`,
                          letterSentLine(p.key),
                        ]}
                        letter={letterActions.includes(p.key) ? {
                          label: `${p.label} referral letter`,
                          actions: (
                            <CardAction inline gold={goldAction === `letter:${p.key}`} to={`${base}/letter/${p.key}`}>
                              {viewLetterLabel(p.key)}
                            </CardAction>
                          ),
                        } : null}
                      />
                    ))
                  ) : (
                    <>
                      <HarbourStageStrip pathway="referral" currentKey="inProgress" />
                      <RecordRow
                        label="Parent or carer forms"
                        chip={refParentChipShown && { ...refParentChipShown, tone: 'referral' }}
                        action={refAction}
                      />
                      <RecordRow label="School forms" chip={refSchoolChip && { ...refSchoolChip, tone: 'referral' }} />
                    </>
                  )}
                  {/* The family's documents (TDI-579, D-05): inside the
                      referral, under the forms; read-only once it is sent. */}
                  {documentsOn && (
                    <HarbourDocumentsBlock
                      summary={documents.summary}
                      loading={documents.loading}
                      error={documents.error}
                      onRetry={documents.reload}
                      onAdd={() => navigate(`${base}/documents/add`)}
                      onRemove={removal.setRemoving}
                    />
                  )}
                  {refSent && <ServiceContact />}
                </>
              ) : (
                <p className="family-pathway-card__unavailable-line">Not started by the school</p>
              )}
            </section>

            {/* The family's profile sharing answer on the laptop record (C-1
                desktop), changeable until the outcome letter (TDI-482). A
                combined referral has one answer; its stage is the furthest
                pathway's. */}
            {referral && !referral.stopped && (
              <div className="family-record__entry">
                <FamilyProfileSharing
                  patient={patient}
                  state={(referral.pathways || []).some((p) => p.status === 'outcomeAvailable' || p.status === 'closed')
                    ? 'outcomeAvailable'
                    : ((referral.pathways || [])[0] || {}).status || 'inProgress'}
                  orgId={orgId}
                  patientRecordId={patientRecordId}
                  childName={firstName}
                  concernLabel={concernWords ? `${concernWords} referral` : 'Referral'}
                  isDesktop
                  isSecondary={isSecondaryPupil(pupil)}
                  onSaved={() => { if (typeof reload === 'function') reload({ silent: true }); }}
                />
              </div>
            )}
            {answersDownloadEntry && <div className="family-record__entry">{answersDownloadEntry}</div>}
            {reconsentEntry && <div className="family-record__entry">{reconsentEntry}</div>}
            {withdrawUi && <div className="family-record__entry">{withdrawUi}</div>}

            <div className="family-record__support">
              <FamilyCrisisLine childName={firstName} className="family-crisis--wide" callButton={false} />
            </div>
          </div>
          {/* Inside the flyout, as the consent page does: the flyout is a
              Radix dialog that hides its siblings from assistive tech, so a
              dialog beside it would be unreachable to a screen reader. */}
          {reconsentUi}
          {removal.removing && (
            <HarbourModal
              title={REMOVE_TITLE}
              confirmLabel={REMOVE_CONFIRM}
              confirmKind="warning"
              busy={removal.busy}
              onConfirm={removal.confirm}
              onClose={removal.cancel}
            >
              <p className="harbour-body-16">{removeBody(removal.removing.description)}</p>
            </HarbourModal>
          )}
        </HarbourFlyout>
      </FamilyPortalShell>
    );
  }

  // ── Mobile: the stacked pathway cards ─────────────────────────────────────
  return (
    <FamilyPortalShell userName={userName} back={back} title={child.name}>
      {reconsentUi}
      <div className="family-page-head">
        <h1 className="family-page-head__title" data-family-title-marker="">{child.name}</h1>
        {child.meta && <p className="family-page-head__meta">{child.meta}</p>}
      </div>

      {reportPendingNotice}
      {waitingForSchoolNotice}

      <div className="family-pathway-cards">
        {sen ? (
          <section className="family-pathway-card family-pathway-card--sen" aria-label="SEND pathway">
            <div className="family-pathway-card__head">
              <h2 className="family-pathway-card__title">
                <HarbourPathwayMark pathway="sen" />
                SEND pathway
              </h2>
              <p className="family-pathway-card__for">{SEND_PURPOSE_LINE}</p>
            </div>
            {senWithdrawn && senWithdrawnNotice}
            {(!sen.stopped || senWithdrawn) && (
              <HarbourStageStrip pathway="sen" currentKey={senWithdrawn ? senWithdrawnStage : sen.stage} />
            )}
            <div className="family-pathway-card__statuses">
              {senWithdrawn ? (
                sen.reportAvailable ? (
                  <StatusRow label={`${firstName}’s profile`} chip={{ status: 'reportAvailable', tone: 'sen' }} />
                ) : (
                  <>
                    <StatusRow label="Parent or carer forms" chip={senParentChipShown && { ...senParentChipShown, tone: 'sen' }} />
                    <StatusRow label="School forms" chip={senSchoolChip && { ...senSchoolChip, tone: 'sen' }} />
                  </>
                )
              ) : sen.stopped ? (
                <HarbourStatusChip status={stopChipStatus(sen.stopReason)} />
              ) : sen.reportAvailable ? (
                <StatusRow
                  label="Strengths and needs profile"
                  chip={{ status: 'reportAvailable', tone: 'sen' }}
                />
              ) : (
                <>
                  <StatusRow label="Parent or carer forms" chip={senParentChipShown && { ...senParentChipShown, tone: 'sen' }} />
                  <StatusRow label="School forms" chip={senSchoolChip && { ...senSchoolChip, tone: 'sen' }} />
                </>
              )}
            </div>
            {senConsentNeeded && (
              <CardAction
                gold={goldAction === 'sen:consent'}
                to={`${base}/consent/sen`}
                arrow={false}
              >
                Agree to the SEND pathway
              </CardAction>
            )}
            {senFormsToDo && (
              <CardAction gold={goldAction === 'sen:forms'} to={`${base}/forms/sen`}>
                {senFormsLabel}
              </CardAction>
            )}
            {!sen.stopped && confirmOn('sen') && confirmAction('sen', goldAction === 'sen:confirm', false)}
            {(!sen.stopped || senWithdrawn) && sen.reportAvailable && (
              <CardAction gold={goldAction === 'sen:report'} to={`/rms/patient/${patientRecordId}/hfp`}>
                View strengths and needs profile
              </CardAction>
            )}
            {senWithdrawn && !sen.reportAvailable && (
              <CardAction to={`${base}/forms/sen`}>View your answers</CardAction>
            )}
            {senReconsentEntry}
            {senWithdrawUi}
            <p className="family-pathway-card__contact">{sendContactLine(firstName)}</p>
          </section>
        ) : (
          <section className="family-pathway-card family-pathway-card--unavailable" aria-label="SEND pathway">
            <div className="family-pathway-card__head">
              <h2 className="family-pathway-card__title">
                <HarbourPathwayMark pathway="sen" />
                SEND pathway
              </h2>
            </div>
            <p className="family-pathway-card__unavailable-line">{senUnavailableLine}</p>
          </section>
        )}

        {referral ? (
          <section className="family-pathway-card family-pathway-card--referral" aria-label="Referral pathway">
            <div className="family-pathway-card__head">
              <h2 className="family-pathway-card__title">
                <HarbourPathwayMark pathway="referral" />
                Referral pathway
              </h2>
              {referral.forLine && <p className="family-pathway-card__for">{referral.forLine}</p>}
            </div>
            {referral.stopped ? (
              <>
                <div className="family-pathway-card__statuses">
                  <HarbourStatusChip status={stopChipStatus(referral.stopReason, 'referral')} />
                </div>
                {answersDownloadEntry}
                {reconsentEntry}
              </>
            ) : refSent ? (
              <div className="family-referral-rows">
                {(referral.pathways || []).map((p) => (
                  <div key={p.key} className="family-referral-row">
                    <Link
                      to={`${base}/referral/${p.key}`}
                      className="family-referral-row__main"
                      aria-label={`${p.label} referral`}
                    >
                      <span className="family-referral-row__text">
                        <span className="family-referral-row__title">{`${p.label} referral`}</span>
                        <HarbourStatusChip status={p.status} tone="referral" />
                        {letterSentLine(p.key) && (
                          <span className="family-referral-row__date">{letterSentLine(p.key)}</span>
                        )}
                      </span>
                      <HarbourIcon name="chevron-right" size={24} />
                    </Link>
                    {letterActions.includes(p.key) && (
                      <CardAction gold={goldAction === `letter:${p.key}`} to={`${base}/letter/${p.key}`}>
                        {viewLetterLabel(p.key)}
                      </CardAction>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="family-pathway-card__statuses">
                  <StatusRow label="Parent or carer forms" chip={refParentChipShown && { ...refParentChipShown, tone: 'referral' }} />
                  <StatusRow label="School forms" chip={refSchoolChip && { ...refSchoolChip, tone: 'referral' }} />
                </div>
                {refConsentNeeded && (
                  <CardAction
                    gold={goldAction === 'referral:consent'}
                    to={`${base}/consent/referral`}
                    arrow={false}
                  >
                    Agree to the referral
                  </CardAction>
                )}
                {refFormsToDo && (
                  <CardAction gold={goldAction === 'referral:forms'} to={`${base}/forms/referral`}>
                    {refFormsLabel}
                  </CardAction>
                )}
                {confirmOn('referral') && confirmAction('referral', goldAction === 'referral:confirm', false)}
              </>
            )}
            {/* Documents (TDI-579, D-06 Q1 and Q5): one row that opens the
                family's Documents screen, with how many they have added. */}
            {documentsOn && !(documents.summary && documents.summary.onReferralPathway === false) && (
              <Link to={`${base}/documents`} className="family-documents-row">
                <span className="family-documents-row__text">
                  <span className="family-documents-row__title">Documents</span>
                  {documents.summary && (
                    <span className="family-documents-row__count">
                      {documentsCountLabel((documents.summary.documents || []).length)}
                    </span>
                  )}
                </span>
                <HarbourIcon name="chevron-right" size={24} />
              </Link>
            )}
            {withdrawUi}
          </section>
        ) : (
          <section className="family-pathway-card family-pathway-card--unavailable" aria-label="Referral pathway">
            <div className="family-pathway-card__head">
              <h2 className="family-pathway-card__title">
                <HarbourPathwayMark pathway="referral" />
                Referral pathway
              </h2>
            </div>
            <p className="family-pathway-card__unavailable-line">Not started by the school</p>
          </section>
        )}
      </div>

      <FamilyCrisisLine childName={firstName} />
    </FamilyPortalShell>
  );
}
